/**
 * G2eGente - Dashboard de Avaliações de Desempenho
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI, performanceCyclesAPI, performanceReviewsAPI } from '../../lib/api';
import { useFeatureAccess, BlockedFeatureOverlay } from '../../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PerformanceManual } from '../../components/UserManual';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, 
  Loader2,
  ClipboardCheck,
  Calendar,
  Users,
  TrendingUp,
  Play,
  Pause,
  Eye,
  BarChart3,
  CheckCircle2,
  Clock,
  AlertCircle,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CYCLE = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700', icon: Clock },
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700', icon: Play },
  encerrado: { label: 'Encerrado', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 }
};

const CLASSIFICACAO_COLORS = {
  excepcional: 'bg-emerald-100 text-emerald-700',
  supera: 'bg-green-100 text-green-700',
  atende: 'bg-yellow-100 text-yellow-700',
  abaixo: 'bg-red-100 text-red-700'
};

export default function PerformanceDashboard() {
  const navigate = useNavigate();
  const { hasAccess, loading: featureLoading } = useFeatureAccess('avaliacao_desempenho');
  const [loading, setLoading] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('all');
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  // Check feature access
  if (!hasAccess && !featureLoading && !loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <BlockedFeatureOverlay 
          feature="avaliacao_desempenho"
          title="Avaliação de Desempenho Bloqueada"
          description="O módulo de Avaliação de Desempenho não está liberado para sua conta. Entre em contato com o administrador para solicitar acesso a esta funcionalidade premium."
        />
      </DashboardLayout>
    );
  }

  const fetchData = async () => {
    try {
      const [cyclesRes, reviewsRes, employeesRes] = await Promise.all([
        performanceCyclesAPI.getAll(),
        performanceReviewsAPI.getAll(),
        employeesAPI.getAll()
      ]);
      setCycles(cyclesRes.data);
      setReviews(reviewsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCycle = async (cycleId) => {
    try {
      await performanceCyclesAPI.start(cycleId);
      toast.success('Ciclo iniciado com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar ciclo');
    }
  };

  const handleCloseCycle = async (cycleId) => {
    try {
      await performanceCyclesAPI.close(cycleId);
      toast.success('Ciclo encerrado com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao encerrar ciclo');
    }
  };

  const activeCycles = cycles.filter(c => c.status === 'ativo');
  const filteredReviews = selectedCycle === 'all' 
    ? reviews 
    : reviews.filter(r => r.cycle_id === selectedCycle);

  const stats = {
    totalCycles: cycles.length,
    activeCycles: activeCycles.length,
    totalReviews: reviews.length,
    finalized: reviews.filter(r => r.status === 'finalizado').length,
    pending: employees.filter(e => e.status === 'ativo').length - reviews.filter(r => r.status === 'finalizado').length
  };

  // Calculate average scores
  const finalizedReviews = reviews.filter(r => r.status === 'finalizado' && r.media_geral);
  const avgScore = finalizedReviews.length > 0
    ? (finalizedReviews.reduce((acc, r) => acc + r.media_geral, 0) / finalizedReviews.length).toFixed(2)
    : 0;

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="performance-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Avaliações de Desempenho</h1>
            <p className="text-slate-600">Gerencie ciclos e avaliações da sua equipe</p>
          </div>
          <div className="flex gap-3">
            <PerformanceManual />
            <Button 
              variant="outline"
              onClick={() => navigate('/company/performance/cycles/new')}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Novo Ciclo
            </Button>
            {activeCycles.length > 0 && (
              <Button 
                onClick={() => navigate('/company/employees')}
                className="bg-green-600 hover:bg-green-700"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Avaliar Funcionário
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Ciclos Ativos</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.activeCycles}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Avaliações Finalizadas</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.finalized}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Pendentes</p>
                  <p className="text-2xl font-bold text-slate-900">{Math.max(0, stats.pending)}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Média Geral</p>
                  <p className="text-2xl font-bold text-slate-900">{avgScore}/5</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="cycles">Ciclos</TabsTrigger>
            <TabsTrigger value="reviews">Avaliações</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {activeCycles.length === 0 ? (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="py-12 text-center">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Nenhum ciclo de avaliação ativo
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Crie um ciclo de avaliação para começar a avaliar sua equipe
                  </p>
                  <Button 
                    onClick={() => navigate('/company/performance/cycles/new')}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeiro Ciclo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Active Cycles */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Ciclos Ativos</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {activeCycles.map(cycle => (
                      <div key={cycle.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900">{cycle.nome}</h4>
                          <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-3">
                          {cycle.periodo_inicio} a {cycle.periodo_fim}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">
                            {cycle.avaliacoes_concluidas || 0}/{cycle.total_funcionarios || 0} avaliações
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCloseCycle(cycle.id)}
                          >
                            <Pause className="w-4 h-4 mr-1" />
                            Encerrar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Recent Reviews */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Avaliações Recentes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reviews.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">
                        Nenhuma avaliação realizada ainda
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {reviews.slice(0, 5).map(review => (
                          <div 
                            key={review.id} 
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors ${
                              review.status === 'rascunho' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
                            }`}
                            onClick={() => navigate(`/company/performance/reviews/${review.id}`)}
                          >
                            <div>
                              <p className="font-medium text-slate-900">{review.employee_nome}</p>
                              <p className="text-sm text-slate-500">{review.cycle_nome}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {review.media_geral && (
                                <Badge className={CLASSIFICACAO_COLORS[review.classificacao] || 'bg-slate-100'}>
                                  {review.media_geral.toFixed(1)}/5
                                </Badge>
                              )}
                              {review.status === 'rascunho' ? (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-amber-600 border-amber-300 hover:bg-amber-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/company/employees/${review.employee_id}/evaluate?review=${review.id}&cycle=${review.cycle_id}`);
                                  }}
                                  data-testid={`edit-draft-${review.id}`}
                                >
                                  <Pencil className="w-3 h-3 mr-1" />
                                  Continuar
                                </Button>
                              ) : (
                                <Badge variant="default">
                                  Finalizado
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Cycles Tab */}
          <TabsContent value="cycles" className="space-y-4">
            {cycles.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Nenhum ciclo cadastrado
                  </h3>
                  <Button 
                    onClick={() => navigate('/company/performance/cycles/new')}
                    className="mt-4 bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Ciclo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              cycles.map(cycle => {
                const StatusIcon = STATUS_CYCLE[cycle.status]?.icon || Clock;
                return (
                  <Card key={cycle.id} className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            cycle.status === 'ativo' ? 'bg-green-100' : 
                            cycle.status === 'encerrado' ? 'bg-blue-100' : 'bg-slate-100'
                          }`}>
                            <StatusIcon className={`w-6 h-6 ${
                              cycle.status === 'ativo' ? 'text-green-600' : 
                              cycle.status === 'encerrado' ? 'text-blue-600' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-slate-900">{cycle.nome}</h3>
                              <Badge className={STATUS_CYCLE[cycle.status]?.color}>
                                {STATUS_CYCLE[cycle.status]?.label}
                              </Badge>
                            </div>
                            <p className="text-slate-600">{cycle.periodo_inicio} a {cycle.periodo_fim}</p>
                            <div className="flex gap-4 mt-2 text-sm text-slate-500">
                              <span>Periodicidade: {cycle.periodicidade}</span>
                              <span>Avaliações: {cycle.avaliacoes_concluidas || 0}/{cycle.total_funcionarios || 0}</span>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">Técnicas: {cycle.peso_competencias_tecnicas}%</Badge>
                              <Badge variant="outline">Comportamentais: {cycle.peso_competencias_comportamentais}%</Badge>
                              <Badge variant="outline">DISC: {cycle.peso_disc}%</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {cycle.status === 'rascunho' && (
                            <Button 
                              size="sm"
                              onClick={() => handleStartCycle(cycle.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Iniciar
                            </Button>
                          )}
                          {cycle.status === 'ativo' && (
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleCloseCycle(cycle.id)}
                            >
                              <Pause className="w-4 h-4 mr-1" />
                              Encerrar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-4">
            <div className="flex items-center justify-between">
              <Select value={selectedCycle} onValueChange={setSelectedCycle}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Filtrar por ciclo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os ciclos</SelectItem>
                  {cycles.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filteredReviews.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Nenhuma avaliação encontrada
                  </h3>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredReviews.map(review => (
                  <Card key={review.id} className="border-slate-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                            <span className="font-semibold text-cyan-700">
                              {review.employee_nome?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{review.employee_nome}</h4>
                            <p className="text-sm text-slate-500">
                              {review.employee_cargo} • {review.cycle_nome}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Média Geral</p>
                            <p className="font-bold text-slate-900">
                              {review.media_geral?.toFixed(2) || '-'}/5
                            </p>
                          </div>
                          {review.classificacao && (
                            <Badge className={CLASSIFICACAO_COLORS[review.classificacao]}>
                              {review.classificacao.charAt(0).toUpperCase() + review.classificacao.slice(1)}
                            </Badge>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/company/performance/reviews/${review.id}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
